import { useCallback, useEffect, useRef, useState } from 'react';
import type { Pagina } from '@kodica/shared';

import { ErrorApi, pedir } from './api';

/**
 * Carga de datos para las tablas del panel.
 *
 * Deliberadamente pequeño en vez de TanStack Query: el panel son ocho tablas
 * que se recargan al guardar, no una app con caché compartida entre vistas.
 * Si más adelante hacen falta invalidaciones cruzadas, cambiar esto por una
 * librería es un reemplazo local y no una reescritura.
 */
export interface Recurso<T> {
  datos: T | null;
  cargando: boolean;
  error: string | null;
  recargar: () => void;
}

export function useRecurso<T>(ruta: string | null): Recurso<T> {
  const [datos, setDatos] = useState<T | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  /**
   * Descarta los datos en el mismo render en que cambia la ruta.
   *
   * Sin esto, al cambiar de pestaña el componente se pinta una vez con los
   * datos de la pestaña anterior —el efecto que pone `cargando` todavía no ha
   * corrido— y la tabla de residentes recibe filas de unidades. El síntoma es
   * una pantalla en blanco con "Cannot read properties of undefined".
   *
   * Es el patrón de React de ajustar estado durante el render: se ejecuta
   * antes de que los hijos se pinten, así que nunca ven la combinación mala.
   */
  const [rutaPintada, setRutaPintada] = useState(ruta);
  if (ruta !== rutaPintada) {
    setRutaPintada(ruta);
    setDatos(null);
    setCargando(ruta !== null);
    setError(null);
  }

  // Descarta la respuesta de una petición vieja que llegue tarde: al teclear
  // en el buscador salen varias en vuelo y la última en llegar no siempre es
  // la última que se pidió.
  const vigente = useRef(0);

  useEffect(() => {
    if (ruta === null) {
      setCargando(false);
      return;
    }

    const mia = ++vigente.current;
    setCargando(true);
    setError(null);

    pedir<T>(ruta)
      .then((r) => {
        if (vigente.current === mia) setDatos(r);
      })
      .catch((e) => {
        if (vigente.current !== mia) return;
        setError(e instanceof ErrorApi ? e.message : 'No se pudo conectar con el servidor');
      })
      .finally(() => {
        if (vigente.current === mia) setCargando(false);
      });
  }, [ruta, version]);

  const recargar = useCallback(() => setVersion((v) => v + 1), []);

  return { datos, cargando, error, recargar };
}

/** Arma `?pagina=1&porPagina=50&buscar=…` sin dejar parámetros vacíos. */
export function consulta(
  base: string,
  params: Record<string, string | number | boolean | undefined>,
) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '' && v !== false) qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${base}?${s}` : base;
}

export type { Pagina };

/**
 * Retrasa el valor mientras el usuario sigue tecleando. Sin esto, cada letra
 * del buscador dispara una consulta contra la base de la caseta.
 */
export function useRetardo<T>(valor: T, ms = 300): T {
  const [retrasado, setRetrasado] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setRetrasado(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return retrasado;
}
